// Derivation rules for the "Usage & limits" table on the Restrictions tab
// (ENG2-2461 / T8 of ENG2-2452).
//
// The account status payload (`GET /customer/plan/:id`) carries `caps` (the
// editable limits, resolved from restriction_templates / restriction_exceptions)
// and `counters` (the live restriction_status columns). The counters do not all
// mean the same thing, which is why the old screen was unreadable:
//
//   remaining  monthly allowances count DOWN and reset on the 1st
//              (filter exports, monitoring exports)
//   used       the monitored-companies counter is a live count that counts UP
//   users      the number of users comes from the users list, not the API
//   seeded     lookups and credit ratings: RisikaAPI seeds the counter with
//              INT max (2,147,483,647) on every plan sync and reset, then
//              decrements it per request. Used = INT max − counter, i.e.
//              "requests since the last reset". A counter that cannot have
//              come from that seed (used ≥ 1e9) was edited by hand, so we say
//              "Not tracked" rather than print nonsense.
//   nordic     lookups: unique companies once RisikaAPI ships
//              `counters.nordic_lookups_used` (T6); until then, seeded.
//   untracked  credit reports: nothing decrements the counter today
//   none       search_exports_limit is a size limit, not an allowance
//
// Every row is normalised here into Cap / Used / Remaining / Percent so the UI
// renders them all the same way. (No "resets" information: the reset schedule
// cannot be verified in production, so it is not shown — ENG2-2456 item 6.)

import { isUnlimitedCap, UNLIMITED_CAP, usagePercent } from 'utils/usage'

export { UNLIMITED_CAP }

// A seeded counter is INT max minus the requests made since the last reset.
// No customer makes a billion requests in a period; anything above this was
// written to the database by hand (see ENG2-2456 item 4 for customer 8894).
export const MAX_DERIVED_USAGE = 1_000_000_000

export type UsageGroup = 'lookups' | 'monthly' | 'capacity' | 'limit'
export type UsageKind =
  | 'remaining'
  | 'used'
  | 'users'
  | 'seeded'
  | 'nordic'
  | 'untracked'
  | 'none'

export type UntrackedReason =
  | 'never_counted'
  | 'counter_out_of_range'
  | 'counter_missing'

export type UsageRowDefinition = {
  key: string
  label: string
  group: UsageGroup
  usage: UsageKind
  tooltip?: string
  // Rows that only make sense for legacy plans (T0 / D2: credit ratings are
  // not tracked for the 2026 packages).
  hideForActivePlans?: boolean
}

export type UsageInput = {
  caps: Record<string, number | undefined>
  counters: Record<string, number | undefined>
  usersLength: number
  plan: string | undefined | null
}

export type UsageRow = {
  key: string
  label: string
  group: UsageGroup
  usage: UsageKind
  cap: number
  unlimited: boolean
  // false for 'none': the row has no Used / Remaining columns at all.
  hasUsage: boolean
  // false when we cannot honestly report usage ("Not tracked").
  tracked: boolean
  untrackedReason?: UntrackedReason
  used: number | null
  remaining: number | null
  percent: number | null
  // Shown next to Used when the number needs qualifying (seeded counters).
  usageNote?: string
  // A monthly counter above its cap: the cap was lowered mid-period and the
  // counter re-syncs on the next reset.
  resync: boolean
  tooltip?: string
}

export const SEEDED_NOTE = 'Requests since the last reset'
export const NORDIC_PHASE1_NOTE =
  'Requests since the last reset, not unique companies'

const counterValue = (
  counters: UsageInput['counters'],
  key: string,
): number | undefined => {
  const value = counters[key]
  return typeof value === 'number' && !Number.isNaN(value) ? value : undefined
}

const isMetered = (counters: UsageInput['counters']): boolean =>
  typeof counters.nordic_lookups_used === 'number'

export const buildUsageRow = (
  definition: UsageRowDefinition,
  input: UsageInput,
): UsageRow => {
  const cap = input.caps[definition.key] ?? 0
  const unlimited = isUnlimitedCap(cap)
  const metered = definition.usage === 'nordic' && isMetered(input.counters)

  const row: UsageRow = {
    key: definition.key,
    label: definition.label,
    group: definition.group,
    usage: definition.usage,
    cap,
    unlimited,
    hasUsage: definition.usage !== 'none',
    tracked: false,
    used: null,
    remaining: null,
    percent: null,
    resync: false,
    tooltip: definition.tooltip,
  }

  const finish = (used: number, remainingFromApi?: number): UsageRow => {
    const remaining =
      remainingFromApi !== undefined
        ? remainingFromApi
        : Math.max(cap - used, 0)
    return {
      ...row,
      tracked: true,
      used,
      remaining,
      percent: usagePercent(used, cap),
    }
  }

  const seeded = (note: string): UsageRow => {
    const counter = counterValue(input.counters, definition.key)
    if (counter === undefined) {
      return { ...row, untrackedReason: 'counter_missing' }
    }
    const used = UNLIMITED_CAP - counter
    if (used < 0 || used >= MAX_DERIVED_USAGE) {
      return { ...row, untrackedReason: 'counter_out_of_range' }
    }
    return { ...finish(used), usageNote: note }
  }

  switch (definition.usage) {
    case 'remaining': {
      // A missing counter means "nothing consumed yet", never "all consumed".
      const remaining = counterValue(input.counters, definition.key) ?? cap
      if (remaining > cap) {
        return { ...finish(0, cap), resync: true }
      }
      return finish(cap - remaining, remaining)
    }
    case 'used':
      return finish(counterValue(input.counters, definition.key) ?? 0)
    case 'users':
      return finish(input.usersLength)
    case 'seeded':
      return seeded(SEEDED_NOTE)
    case 'nordic': {
      if (!metered) return seeded(NORDIC_PHASE1_NOTE)
      const used = input.counters.nordic_lookups_used as number
      return finish(used, counterValue(input.counters, definition.key))
    }
    case 'untracked':
      return { ...row, untrackedReason: 'never_counted' }
    case 'none':
    default:
      return row
  }
}
